import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, ArrowUpRight } from "lucide-react";
import PageBanner from "../components/PageBanner";
import { services, serviceDetails, BOOK_MEETING_URL } from "../mock";

const ServiceDetail = () => {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);
  if (!service) return <Navigate to="/services" replace />;
  const d = serviceDetails[service.id] || {};
  const others = services.filter((s) => s.id !== id);

  return (
    <>
      <PageBanner
        title={service.title}
        crumbs={[{ label: "Services", to: "/services" }, { label: "Detail" }]}
      />
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 md:px-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <span className="rounded-full border border-[#B8862F]/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#B8862F]">
              {service.category}
            </span>
            {d.tagline && (
              <p
                data-testid="service-detail-title"
                className="mt-4 text-xl font-medium leading-snug text-[#0F2A4A] md:text-2xl"
              >
                {d.tagline}
              </p>
            )}
            {d.intro && (
              <p className="mt-4 text-[15px] leading-relaxed text-slate-600">
                {d.intro}
              </p>
            )}

            {/* Why India, Why Now? */}
            {d.whyIndia && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-[#0F2A4A]">
                  {d.whyIndia.heading}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                  {d.whyIndia.text}
                </p>
                <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E4545F]">
                  Recent Trade Agreements
                </p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {d.whyIndia.agreements.map((a) => (
                    <span
                      key={a.name}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm shadow-sm"
                    >
                      <span className="font-medium text-[#0F2A4A]">{a.name}</span>
                      <span className="rounded-full bg-[#E4545F]/10 px-2 py-0.5 text-[11px] font-semibold text-[#E4545F]">
                        {a.year}
                      </span>
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-xs italic leading-relaxed text-slate-400">
                  {d.whyIndia.note}
                </p>
                <p className="mt-6 text-[15px] leading-relaxed text-slate-600">
                  {d.whyIndia.closing}
                </p>
              </div>
            )}

            {/* Deliverables */}
            {d.deliver && (
              <div className="mt-10">
                <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#E4545F]">
                  {d.deliverHeading}
                </h3>
                <div className="mt-5 space-y-4">
                  {d.deliver.map((it) => (
                    <div key={it.title} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#B8862F]" />
                      <div>
                        <span className="block font-semibold text-[#0F2A4A]">
                          {it.title}
                        </span>
                        <span className="mt-0.5 block text-sm leading-relaxed text-slate-600">
                          {it.desc}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* On-the-ground operations callout */}
            {d.operationsNote && (
              <div className="mt-8 rounded-xl border-l-4 border-[#B8862F] bg-[#F6F7F9] p-6">
                <p className="text-[15px] font-medium leading-relaxed text-[#0F2A4A]">
                  {d.operationsNote}
                </p>
              </div>
            )}

            {/* Process / approach panel */}
            {d.process && (
              <div className="mt-10 rounded-2xl bg-[#0F2A4A] p-7 md:p-8">
                <h3 className="text-base font-semibold text-white md:text-lg">
                  {d.process.heading}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/80">
                  {d.process.text}
                </p>
                {d.process.note && (
                  <p className="mt-3 text-sm italic leading-relaxed text-[#F6A9AE]">
                    {d.process.note}
                  </p>
                )}
              </div>
            )}

            {/* Why Continex */}
            {d.whyContinex && (
              <div className="mt-10">
                <h3 className="text-lg font-semibold text-[#0F2A4A]">
                  Why Continex?
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                  {d.whyContinex}
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="mt-10">
              <Link
                to="/contact"
                data-testid="service-detail-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#E4545F] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#c9434e]"
              >
                {d.ctaLabel || "Get in touch"}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <aside>
            <div className="rounded-2xl border border-slate-100 p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-[#0F2A4A]">
                Other Services
              </h3>
              <ul className="space-y-1">
                {others.map((o) => (
                  <li key={o.id}>
                    <Link
                      to={`/services/${o.id}`}
                      className="flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm text-[#0F2A4A] transition-colors duration-150 hover:bg-slate-50 hover:text-[#E4545F]"
                    >
                      {o.title}
                      <ArrowRight className="h-4 w-4 flex-shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 rounded-2xl bg-[#0F2A4A] p-6 text-center text-white">
              <h3 className="text-lg font-semibold">Need help?</h3>
              <p className="mt-2 text-sm text-white/70">
                Book an introductory call with our team.
              </p>
              <a
                href={BOOK_MEETING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#0F2A4A] transition-all duration-200 hover:-translate-y-0.5"
              >
                Book a Meeting <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
